import { api } from "../api";
import { el } from "../dom";
import { storeAdmin } from "../identity";
import { asStateWrap } from "../../shared/sync";
import { mountEventForm } from "../eventform";
import type { EventState } from "../../shared/types";

function asCreate(v: unknown): { state: EventState; adminToken: string } | null {
  const wrap = asStateWrap(v);
  if (!wrap) return null;
  if (typeof v !== "object" || v === null) return null;
  const rec = v as Record<string, unknown>;
  if (typeof rec.adminToken !== "string") return null;
  return { state: wrap.state, adminToken: rec.adminToken };
}

export function renderCreate(root: HTMLElement): { destroy(): void } {
  const wrap = el("div", { class: "wrap" });
  const panel = el("div", { class: "panel" });
  panel.append(
    el("h1", { text: "New poll" }),
    el("p", { class: "muted", text: "Share a link. Paint when you can. Lock a time." }),
  );

  const form = mountEventForm({
    titleTestId: "create-title",
    noteTestId: "create-note",
    tzTestId: "create-tz",
    startTestId: "create-start",
    endTestId: "create-end",
    lengthTestId: "create-length",
    deadlineTestId: "create-deadline",
  });
  const err = el("p", { class: "error", "data-testid": "create-error" });
  const submit = el("button", { class: "primary", type: "submit", text: "Create event", "data-testid": "create-submit" });
  const retry = el("button", { type: "button", text: "Try again" });
  retry.hidden = true;
  const htmlForm = el("form");
  htmlForm.append(form.root, err, el("p", null, submit, retry));

  async function create(): Promise<void> {
    err.textContent = "";
    retry.hidden = true;
    const body = form.read();
    if ("error" in body) {
      err.textContent = body.error;
      return;
    }
    submit.disabled = true;
    try {
      const res = await api("POST", "/api/events", { body, parse: asCreate });
      if (!res.ok) {
        err.textContent = res.error;
        if (res.status === 0) retry.hidden = false;
        return;
      }
      const id = res.data.state.event.id;
      // When storage is blocked, a plain navigation to /e/<id> would lose the admin token that
      // only ever lived in this soon-to-be-torn-down page's memory, orphaning the poll at birth.
      // Carrying it in the hash lets the event view verify it, hold it in memory, and show the
      // existing "copy your organizer link" warning instead.
      const persisted = storeAdmin(id, res.data.adminToken);
      location.assign(persisted ? `/e/${id}` : `/e/${id}#admin=${res.data.adminToken}`);
    } catch (e) {
      console.error(e);
      err.textContent = "Something went wrong. Try again.";
      retry.hidden = false;
    } finally {
      submit.disabled = false;
    }
  }

  htmlForm.addEventListener("submit", (e) => {
    e.preventDefault();
    void create();
  });
  retry.addEventListener("click", () => {
    void create();
  });
  panel.append(htmlForm);
  wrap.append(panel);
  root.replaceChildren(wrap);
  return { destroy() {} };
}
